import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  LBAlertCircleIcon,
  LBButton,
  LBCard,
  LBCardContent,
  LBCardDescription,
  LBCardHeader,
  LBCardTitle,
  LBLoader
} from '@linearb/buzz'
import LinearbLogo from '../../images/linearb_logo_w_text.svg'
import GoogleLogo from '../../images/google_logo.svg'
import { AUTH_LINREST_API_GATEWAY_URL } from '../../constants/apis'
import useJwt from '../../hooks/useJwt'
import { jwtStorage } from '../../utils/jwtStorage'
import { isJwtExpired } from '../../utils/jwtExpiry'

// Create a mock JWT token with backoffice scopes for development
const createMockJwt = () => {
  const header = { alg: 'HS256', typ: 'JWT' }
  const payload = {
    sub: 'mock-user-123',
    email: 'developer@linearb.io',
    name: 'Mock Developer',
    avatar_url:
      'https://ui-avatars.com/api/?name=Mock+Dev&background=0D8ABC&color=fff&rounded=true',
    backoffice_scopes: JSON.stringify(['admin', 'read']),
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 86400
  }

  const base64UrlEncode = obj =>
    btoa(JSON.stringify(obj))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')

  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.mock-signature`
}

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMockMode = import.meta.env.VITE_USE_MOCKS === 'true'
  const loginError = location.state?.error
  const jwt = useJwt()
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Back from Google restores this page from the bfcache with the button still spinning
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsRedirecting(false)
      }
    }
    window.addEventListener('pageshow', reset)
    return () => window.removeEventListener('pageshow', reset)
  }, [])

  // Redirect during render, not in an effect, so the card never paints
  if (!isJwtExpired(jwt)) {
    return <Navigate to='/' replace />
  }

  const loginRedirect = () => {
    // The redirect chain to Google takes ~1.5s with this page still painted
    setIsRedirecting(true)
    const url = `${AUTH_LINREST_API_GATEWAY_URL}/api/v1/rest/public/auth/identity/backoffice_google_identity`
    window.location.href = url
  }

  const handleMockLogin = () => {
    jwtStorage.set(createMockJwt())
    navigate('/')
  }

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100'>
      <div className='w-full max-w-md px-4'>
        <div className='flex justify-center mb-8'>
          <img
            src={LinearbLogo}
            alt='LinearB'
            className='h-10 dark:brightness-0 dark:invert'
          />
        </div>

        <LBCard className='shadow-lg'>
          <LBCardHeader className='text-center pb-2'>
            <LBCardTitle className='text-xl'>Backoffice Portal</LBCardTitle>
            <LBCardDescription>
              Sign in to access the internal admin tools
            </LBCardDescription>
          </LBCardHeader>
          <div className='px-6 pt-4 pb-2'>
            <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm'>
              <p className='text-blue-800 dark:text-blue-300 font-medium mb-1'>
                ⓘ Support Account Required
              </p>
              <p className='text-blue-700 dark:text-blue-400 text-xs'>
                Please sign in with your support account (e.g.,{' '}
                <span className='font-mono'>name.support@linearb.io</span>)
              </p>
            </div>
          </div>
          {loginError && (
            <div className='px-6 pb-2'>
              <div className='flex flex-col items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-4 text-center'>
                <LBAlertCircleIcon className='h-6 w-6 text-red-500 shrink-0' />
                <p className='text-sm font-semibold text-red-700 dark:text-red-300'>
                  Login Failed
                </p>
                <p className='text-sm text-red-600 dark:text-red-400'>
                  {loginError}
                </p>
              </div>
            </div>
          )}
          <LBCardContent className='space-y-4 pt-4'>
            <LBButton
              variant='outline'
              onClick={loginRedirect}
              disabled={isRedirecting}
              className='w-full flex items-center justify-center gap-3 h-11'
            >
              {isRedirecting ? (
                <>
                  <LBLoader className='h-5 w-5' />
                  Signing in with Google...
                </>
              ) : (
                <>
                  <img src={GoogleLogo} alt='Google' className='h-5 w-5' />
                  Continue with Google
                </>
              )}
            </LBButton>

            {isMockMode && (
              <>
                <div className='relative'>
                  <div className='absolute inset-0 flex items-center'>
                    <span className='w-full border-t' />
                  </div>
                  <div className='relative flex justify-center text-xs uppercase'>
                    <span className='bg-card px-2 text-muted-foreground'>
                      Development
                    </span>
                  </div>
                </div>
                <LBButton
                  variant='secondary'
                  onClick={handleMockLogin}
                  className='w-full'
                >
                  Mock Login (Dev Only)
                </LBButton>
              </>
            )}
          </LBCardContent>
        </LBCard>

        <p className='text-center text-xs text-muted-foreground mt-6'>
          Access restricted to LinearB employees
        </p>
      </div>
    </div>
  )
}

export default LoginPage
